import { stateMap } from '../../constants';

export const LOCALIZED_TEXT = {
  annualReport: {
    label:  'businessProfile.aiAssistant.annualReport.buttonLabel',
    message: 'businessProfile.aiAssistant.annualReport.message'
  },
  amendment: {
    label: 'businessProfile.aiAssistant.amendment.buttonLabel',
    message: 'businessProfile.aiAssistant.amendment.message'

  },
  support: {
    label: 'businessProfile.aiAssistant.support.buttonLabel',
    message: 'businessProfile.aiAssistant.support.message'
  },
  negative: {
    message: 'businessProfile.aiAssistant.negative.message'
  }
};

/**
 * Replaces placeholders in a template string with corresponding values from a data object.
 *
 * Placeholders must be in the format {{key}}. If a key is not found in the data object,
 * it will be replaced with an empty string.
 *
 * @param {string} template - The template string containing placeholders.
 * @param {object} data - An object containing key-value pairs to interpolate into the template.
 * @returns {string} The interpolated string with placeholders replaced by data values.
 */
export const interpolate = (template, data) => template.replace(/{{(.*?)}}/g, (_, key) => {
  const trimmedKey = key.trim();
  return Object.prototype.hasOwnProperty.call(data, trimmedKey) ? data[trimmedKey] : '';
});

export const formatResponseMessage = (message, businessEntity) => {
  if (!message) return '';

  const businessData = {
    businessName: businessEntity.name,
    domesticJurisdiction: stateMap[businessEntity.domesticJurisdiction] ?? 'state'
  };

  return interpolate(message, businessData);
};

/**
 * Generates a response configuration object based on the provided results and context.
 *
 * @param {object} params - The parameters for generating the response config.
 * @param {object} params.results - The results object containing possible keys like `amendments` and `annualReport`.
 * @param {string} params.businessEntity - The business entity to be used in the response message.
 * @param {Function} params.fm - A formatting function for localization messages.
 * @param {string} params.dashboardPath - The base path for dashboard navigation links.
 * @param {boolean} params.hasPositiveResponse - A boolean indicating if the response has a positive outcome.
 * @returns {object} The response configuration object containing `actions` (array) and `message` (string).
 */
export const getResponseConfig = ({
  results, businessEntity, fm, dashboardPath, hasPositiveResponse
}) => {
  const { amendments, annualReport } = results;

  if (!hasPositiveResponse) {
    return { actions: [], message: fm({ id: LOCALIZED_TEXT.negative.message }) };
  }

  const supportAction = {
    label: LOCALIZED_TEXT.support.label,
    to: '/dashboard/help',
    variant: 'outlined'
  };

  if (amendments) {
    return {
      actions: [
        {
          label: LOCALIZED_TEXT.amendment.label,
          to: `${dashboardPath}/products/amendment`
        }
      ],
      message: formatResponseMessage(
        fm({ id: LOCALIZED_TEXT.amendment.message }),
        businessEntity
      )
    };
  }

  if (annualReport) {
    return {
      actions: [
        {
          label: LOCALIZED_TEXT.annualReport.label,
          to: `${dashboardPath}/compliance/annual-reports`
        },
        supportAction
      ],
      message: formatResponseMessage(
        fm({ id: LOCALIZED_TEXT.annualReport.message }),
        businessEntity
      )
    };
  }

  return {
    actions: [supportAction],
    message: formatResponseMessage(
      fm({ id: LOCALIZED_TEXT.support.message }),
      businessEntity
    )
  };
};
